import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, ExternalLink, Save,
  CheckCircle, AlertCircle, Bot,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { botFlowsService, BotState, BotTriggerType } from '@/services/bot-flows.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';

import { WizardStepShell } from './wizard/BotFlowWizardSteps';
import { WizardChatPreview } from './wizard/WizardChatPreview';
import { WizardQuestionBuilder } from './wizard/WizardQuestionBuilder';
import { WizardBranchEditor } from './wizard/WizardBranchEditor';
import type { WizardFlow, WizardQuestion, CollectFieldType } from './wizard/types';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_LABELS = [
  'About',
  'Greeting',
  'Questions',
  'Branches',
  'Handoff',
  'Closing',
  'Review',
];

const TOTAL_STEPS = STEP_LABELS.length;

let _qCounter = 0;
function newQuestionId(): string {
  _qCounter += 1;
  return `q_${_qCounter}`;
}

function defaultFlow(): WizardFlow {
  return {
    name: 'My bot',
    purpose: '',
    triggerType: 'manual',
    greeting: 'Hi {{profileName}}, thanks for reaching out! How can we help?',
    questions: [],
    handoffAssign: 'round_robin',
    handoffMessage: 'New lead — please follow up.',
    closingMessage: "Thanks! Someone from our team will reach out shortly.",
  };
}

// ─── Compile wizard flow → BotFlow states ─────────────────────────────────────

function compileToBotStates(flow: WizardFlow): { states: BotState[]; startStateId: string } {
  const states: BotState[] = [];

  // Greeting
  const greetingId = 'greeting_1';

  // Build question chain IDs up front so we can wire next pointers
  const questionIds: string[] = flow.questions.map((q, i) => {
    if (q.type === 'choice') return `q_${i + 1}_choice`;
    if (q.type === 'collect') return `q_${i + 1}_collect`;
    return `handoff_${i + 1}`;
  });

  // Determine first question (or end)
  const firstNextId = questionIds[0] ?? 'handoff_final';

  // Greeting state
  states.push({
    id: greetingId,
    type: 'message',
    prompt: flow.greeting,
    next: firstNextId,
  });

  // Process questions
  for (let i = 0; i < flow.questions.length; i++) {
    const q = flow.questions[i];
    const qId = questionIds[i];
    const defaultNext = questionIds[i + 1] ?? 'handoff_final';

    if (q.type === 'handoff') {
      states.push({
        id: qId,
        type: 'handoff',
        assignTo: flow.handoffAssign,
        notifyMessage: flow.handoffMessage,
      });
    } else if (q.type === 'collect') {
      const fieldMap: Record<CollectFieldType, BotState['type']> = {
        name: 'collect',
        phone: 'collect',
        email: 'collect',
        number: 'collect',
        text: 'collect',
      };
      const expectedTypeMap: Record<CollectFieldType, 'text' | 'phone' | 'email' | 'number'> = {
        name: 'text',
        phone: 'phone',
        email: 'email',
        number: 'number',
        text: 'text',
      };
      void fieldMap;
      states.push({
        id: qId,
        type: 'collect',
        prompt: q.prompt,
        expectedType: expectedTypeMap[q.collectType ?? 'text'],
        saveAs: q.collectType ?? 'text',
        next: defaultNext,
        ...(q.validationMin !== undefined || q.validationMax !== undefined
          ? { validation: { min: q.validationMin, max: q.validationMax } }
          : {}),
      });
    } else if (q.type === 'choice') {
      // Map each option to a state
      const choiceOptions = (q.options ?? []).map(opt => ({
        label: opt.label,
        value: opt.label.toLowerCase().replace(/\s+/g, '_'),
        nextStateId: (() => {
          if (opt.action === 'handoff') return 'handoff_final';
          if (opt.action === 'end') {
            const endId = `end_${opt.id}`;
            // We'll push this end state below
            return endId;
          }
          return defaultNext;
        })(),
      }));

      states.push({
        id: qId,
        type: 'choice',
        prompt: q.prompt,
        options: choiceOptions,
      });

      // Push terminal end states for options that end
      for (const opt of q.options ?? []) {
        if (opt.action === 'end') {
          const endId = `end_${opt.id}`;
          // Only add if not already added
          if (!states.find(s => s.id === endId)) {
            if (opt.endMessage) {
              const msgId = `msg_${opt.id}`;
              states.push({
                id: msgId,
                type: 'message',
                prompt: opt.endMessage,
                next: endId,
              });
              // Re-point the choice option to the message state
              const choiceState = states.find(s => s.id === qId);
              if (choiceState && choiceState.type === 'choice') {
                const co = choiceState.options.find(co => co.nextStateId === endId);
                if (co) co.nextStateId = msgId;
              }
            }
            states.push({ id: endId, type: 'end', terminalStatus: 'qualified' });
          }
        }
      }
    }
  }

  // Closing message → end
  const closingId = 'closing_msg';
  const endFinalId = 'end_final';

  states.push({
    id: 'handoff_final',
    type: 'handoff',
    assignTo: flow.handoffAssign,
    notifyMessage: flow.handoffMessage,
  });

  if (flow.closingMessage) {
    states.push({
      id: closingId,
      type: 'message',
      prompt: flow.closingMessage,
      next: endFinalId,
    });
  }

  states.push({ id: endFinalId, type: 'end', terminalStatus: 'qualified' });

  return { states, startStateId: greetingId };
}

// ─── Step components ──────────────────────────────────────────────────────────

// Step 1
interface Step1Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const TRIGGER_OPTS: { value: BotTriggerType; label: string; desc: string }[] = [
  { value: 'ad', label: 'Run when someone messages from an ad', desc: 'Triggered by a WhatsApp click-to-message ad' },
  { value: 'lead_ad_form', label: 'Run when someone fills my lead form', desc: 'Triggered by a Facebook lead ad form submission' },
  { value: 'keyword', label: 'Run when someone types a keyword', desc: 'Triggered by a matching inbound message' },
  { value: 'manual', label: 'Run manually for now', desc: "I'll connect a trigger later" },
];

const Step1: React.FC<Step1Props> = ({ flow, onChange }) => (
  <div className="space-y-4">
    <FormField label="Bot name" required>
      <Input
        value={flow.name}
        onChange={e => onChange({ name: e.target.value })}
        placeholder="My bot"
      />
    </FormField>

    <FormField label="One-line purpose" hint="Optional — helps you identify it later">
      <Input
        value={flow.purpose}
        onChange={e => onChange({ purpose: e.target.value })}
        placeholder="e.g. Qualify restaurant reservations"
      />
    </FormField>

    <div>
      <div className="mb-2 text-[13px] font-medium text-text-secondary">When should this bot run?</div>
      <div className="space-y-2">
        {TRIGGER_OPTS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ triggerType: opt.value })}
            className={clsx(
              'w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all',
              flow.triggerType === opt.value
                ? 'border-primary bg-primary-soft/60'
                : 'border-border bg-surface hover:border-primary/40',
            )}
          >
            <div
              className={clsx(
                'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
                flow.triggerType === opt.value
                  ? 'border-primary bg-primary'
                  : 'border-border-strong',
              )}
            />
            <div>
              <div className={clsx(
                'text-sm font-semibold',
                flow.triggerType === opt.value ? 'text-primary' : 'text-text-primary',
              )}>
                {opt.label}
              </div>
              <div className="text-xs text-text-muted">{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

// Step 2
interface Step2Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const VARS = ['{{profileName}}', '{{phone}}'];

const Step2: React.FC<Step2Props> = ({ flow, onChange }) => {
  const insertVar = (v: string) => {
    onChange({ greeting: flow.greeting + v });
  };

  return (
    <div className="space-y-4">
      <FormField label="Greeting message" required>
        <textarea
          rows={4}
          value={flow.greeting}
          onChange={e => onChange({ greeting: e.target.value })}
          className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft resize-none transition-colors"
          placeholder="Hi {{profileName}}, how can we help?"
        />
      </FormField>

      <div>
        <div className="mb-2 text-[12px] font-medium text-text-muted">Insert variable</div>
        <div className="flex flex-wrap gap-1.5">
          {VARS.map(v => (
            <button
              key={v}
              type="button"
              onClick={() => insertVar(v)}
              className="rounded-md border border-primary/30 bg-primary-soft px-2.5 py-1 font-mono text-[11px] text-primary hover:brightness-110 transition-colors"
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Step 3
interface Step3Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const Step3: React.FC<Step3Props> = ({ flow, onChange }) => {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const addQuestion = () => {
    const newQ: WizardQuestion = {
      id: newQuestionId(),
      type: 'collect',
      prompt: '',
      collectType: 'text',
    };
    const updated = [...flow.questions, newQ];
    onChange({ questions: updated });
    setEditingIdx(updated.length - 1);
  };

  const removeQuestion = (idx: number) => {
    const updated = flow.questions.filter((_, i) => i !== idx);
    onChange({ questions: updated });
    setEditingIdx(null);
  };

  const updateQuestion = (idx: number, q: WizardQuestion) => {
    const updated = flow.questions.map((existing, i) => i === idx ? q : existing);
    onChange({ questions: updated });
  };

  return (
    <div className="space-y-4">
      {flow.questions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center">
          <p className="text-sm text-text-muted mb-3">No questions yet. Add one to get started.</p>
          <Button size="sm" variant="soft" iconLeft={<Plus size={13} />} onClick={addQuestion}>
            Add first question
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {flow.questions.map((q, idx) => (
              <div key={q.id} className="rounded-xl border border-border bg-surface overflow-hidden">
                {/* Header row */}
                <div
                  className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-alt transition-colors"
                  onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-alt text-[11px] font-bold text-text-muted">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-text-primary truncate">
                      {q.prompt || (q.type === 'handoff' ? 'Hand off to team' : 'Untitled question')}
                    </div>
                    <div className="text-[11px] text-text-muted capitalize">{q.type}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeQuestion(idx); }}
                    className="shrink-0 text-text-muted hover:text-danger transition-colors"
                    aria-label="Remove question"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Expanded editor */}
                {editingIdx === idx && (
                  <div className="border-t border-border p-4">
                    <WizardQuestionBuilder
                      question={q}
                      onChange={updated => updateQuestion(idx, updated)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={addQuestion}>
            Add another question
          </Button>
        </>
      )}
    </div>
  );
};

// Step 4
interface Step4Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const Step4: React.FC<Step4Props> = ({ flow, onChange }) => {
  const choiceQuestions = flow.questions.filter(q => q.type === 'choice');

  if (choiceQuestions.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-5 text-center text-sm text-text-muted">
        You don't have any multiple-choice questions yet. Go back to Step 3 to add one, or skip to continue.
      </div>
    );
  }

  const updateQuestion = (updatedQ: WizardQuestion) => {
    onChange({
      questions: flow.questions.map(q => q.id === updatedQ.id ? updatedQ : q),
    });
  };

  return (
    <div className="space-y-6">
      {choiceQuestions.map((q, idx) => (
        <div key={q.id}>
          <div className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-text-muted">
            Question {idx + 1}
          </div>
          <WizardBranchEditor question={q} onChange={updateQuestion} />
        </div>
      ))}
    </div>
  );
};

// Step 5
interface Step5Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const Step5: React.FC<Step5Props> = ({ flow, onChange }) => (
  <div className="space-y-4">
    <div>
      <div className="mb-2 text-[13px] font-medium text-text-secondary">
        When should the bot hand off to your team?
      </div>
      <div className="flex flex-col gap-2">
        {([
          { value: 'round_robin' as const, label: 'After the bot has all info', desc: 'Handoff happens once all questions are answered' },
          { value: 'unassigned' as const, label: 'Leave unassigned', desc: 'Place in the unassigned queue for anyone to pick up' },
        ] as const).map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ handoffAssign: opt.value })}
            className={clsx(
              'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all',
              flow.handoffAssign === opt.value
                ? 'border-primary bg-primary-soft/60'
                : 'border-border bg-surface hover:border-primary/40',
            )}
          >
            <div className={clsx(
              'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
              flow.handoffAssign === opt.value ? 'border-primary bg-primary' : 'border-border-strong',
            )} />
            <div>
              <div className={clsx(
                'text-sm font-semibold',
                flow.handoffAssign === opt.value ? 'text-primary' : 'text-text-primary',
              )}>
                {opt.label}
              </div>
              <div className="text-xs text-text-muted">{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>

    <FormField label="Notification message to your team">
      <textarea
        rows={3}
        value={flow.handoffMessage}
        onChange={e => onChange({ handoffMessage: e.target.value })}
        className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft resize-none transition-colors"
        placeholder="New lead — please follow up."
      />
    </FormField>
  </div>
);

// Step 6
interface Step6Props {
  flow: WizardFlow;
  onChange: (patch: Partial<WizardFlow>) => void;
}

const Step6: React.FC<Step6Props> = ({ flow, onChange }) => (
  <div className="space-y-4">
    <FormField label="Thank-you message" required>
      <textarea
        rows={4}
        value={flow.closingMessage}
        onChange={e => onChange({ closingMessage: e.target.value })}
        className="w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft resize-none transition-colors"
        placeholder="Thanks! Someone from our team will reach out shortly."
      />
    </FormField>
    <p className="text-xs text-text-muted">
      This is the last message the bot sends before handing off to your team.
    </p>
  </div>
);

// Step 7 — Review
interface Step7Props {
  flow: WizardFlow;
  saving: boolean;
  onSave: (activate: boolean) => void;
  onOpenInEditor: () => void;
}

const Step7: React.FC<Step7Props> = ({ flow, saving, onSave, onOpenInEditor }) => {
  const choiceCount = flow.questions.filter(q => q.type === 'choice').length;
  const branchCount = flow.questions.reduce(
    (acc, q) => acc + (q.type === 'choice' ? (q.options?.length ?? 0) : 0),
    0,
  );

  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <Bot size={16} />
          </div>
          <div>
            <div className="text-sm font-bold text-text-primary">{flow.name || 'Untitled bot'}</div>
            {flow.purpose && <div className="text-xs text-text-muted">{flow.purpose}</div>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Questions', value: flow.questions.length },
            { label: 'Branches', value: branchCount },
            { label: 'Choice Qs', value: choiceCount },
          ].map(stat => (
            <div key={stat.label} className="rounded-lg bg-surface-alt p-2.5 text-center">
              <div className="text-lg font-bold text-text-primary">{stat.value}</div>
              <div className="text-[11px] text-text-muted">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-success">
          <CheckCircle size={12} />
          Ends with handoff — a person will follow up
        </div>
      </div>

      {/* Validation warnings */}
      {flow.questions.some(q => q.type !== 'handoff' && !q.prompt) && (
        <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning-soft p-3 text-sm text-warning">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          Some questions don't have prompt text. Go back to Step 3 to fill them in.
        </div>
      )}

      {/* Save buttons */}
      <div className="flex flex-col gap-2 pt-1">
        <Button
          loading={saving}
          iconLeft={<CheckCircle size={14} />}
          className="w-full"
          onClick={() => onSave(true)}
        >
          Save and activate
        </Button>
        <Button
          variant="secondary"
          iconLeft={<Save size={14} />}
          className="w-full"
          disabled={saving}
          onClick={() => onSave(false)}
        >
          Save as draft
        </Button>
        <Button
          variant="ghost"
          iconLeft={<ExternalLink size={14} />}
          className="w-full"
          disabled={saving}
          onClick={onOpenInEditor}
        >
          Open in visual editor first
        </Button>
      </div>
    </div>
  );
};

// ─── Main Wizard Component ────────────────────────────────────────────────────

export default function BotFlowWizard() {
  const navigate = useNavigate();
  const { active: activeAccount } = useActiveAccount();
  const [step, setStep] = useState(1);
  const [flow, setFlow] = useState<WizardFlow>(defaultFlow);
  const [saving, setSaving] = useState(false);

  const patchFlow = useCallback((patch: Partial<WizardFlow>) => {
    setFlow(prev => ({ ...prev, ...patch }));
  }, []);

  const goBack = step > 1 ? () => setStep(s => s - 1) : undefined;

  const canProceedStep1 = flow.name.trim().length > 0;
  const canProceedStep2 = flow.greeting.trim().length > 0;
  const canProceedStep6 = flow.closingMessage.trim().length > 0;

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(s => s + 1);
    }
  };

  const handleSave = async (activate: boolean) => {
    setSaving(true);
    try {
      const { states, startStateId } = compileToBotStates(flow);
      const created = await botFlowsService.create({
        name: flow.name,
        description: flow.purpose || undefined,
        accountUid: activeAccount?.accountUid,
        triggerType: flow.triggerType,
        triggerConfig: {},
        states,
        startStateId,
        isActive: activate,
      });
      toast.success(activate ? 'Bot activated!' : 'Draft saved!');
      navigate(`/leads/bot-flows/${created.flowUid}`);
    } catch {
      toast.error('Failed to save bot flow');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenInEditor = async () => {
    setSaving(true);
    try {
      const { states, startStateId } = compileToBotStates(flow);
      const created = await botFlowsService.create({
        name: flow.name,
        description: flow.purpose || undefined,
        accountUid: activeAccount?.accountUid,
        triggerType: flow.triggerType,
        triggerConfig: {},
        states,
        startStateId,
        isActive: false,
      });
      toast.success('Draft saved — opening visual editor');
      navigate(`/leads/bot-flows/${created.flowUid}`);
    } catch {
      toast.error('Failed to save bot flow');
    } finally {
      setSaving(false);
    }
  };

  // Step content
  const stepContent = () => {
    switch (step) {
      case 1:
        return (
          <WizardStepShell
            title="Tell us about this bot"
            subtitle="Give it a name and choose when it should run."
            onBack={goBack}
            onNext={handleNext}
            nextDisabled={!canProceedStep1}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step1 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 2:
        return (
          <WizardStepShell
            title="How will the bot greet customers?"
            subtitle="The first message your bot sends."
            onBack={goBack}
            onNext={handleNext}
            nextDisabled={!canProceedStep2}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step2 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 3:
        return (
          <WizardStepShell
            title="What should the bot ask?"
            subtitle="Add questions to collect information or give choices."
            onBack={goBack}
            onNext={handleNext}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step3 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 4:
        return (
          <WizardStepShell
            title="How does the bot handle different answers?"
            subtitle="For each multiple-choice question, decide what happens per option."
            onBack={goBack}
            onNext={handleNext}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step4 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 5:
        return (
          <WizardStepShell
            title="When do you want a human to take over?"
            subtitle="Configure how and when the conversation is handed off to your team."
            onBack={goBack}
            onNext={handleNext}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step5 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 6:
        return (
          <WizardStepShell
            title="What's the final message to the customer?"
            subtitle="A closing note sent after the bot has collected everything."
            onBack={goBack}
            onNext={handleNext}
            nextDisabled={!canProceedStep6}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step6 flow={flow} onChange={patchFlow} />
          </WizardStepShell>
        );
      case 7:
        return (
          <WizardStepShell
            title="Review your bot"
            subtitle="Everything looks good? Save it or open the visual editor for fine-tuning."
            onBack={goBack}
            onNext={() => handleSave(false)}
            nextLabel="Save draft"
            nextLoading={saving}
            current={step}
            total={TOTAL_STEPS}
            stepLabels={STEP_LABELS}
          >
            <Step7
              flow={flow}
              saving={saving}
              onSave={handleSave}
              onOpenInEditor={handleOpenInEditor}
            />
          </WizardStepShell>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <WhatsAppHeader section="automation"
        title="Step-by-step bot builder"
        breadcrumb="Bot Flows"
        description="Answer a few questions and we'll build your flow — no technical knowledge needed."
        actions={
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<ArrowLeft size={14} />}
            onClick={() => navigate('/leads/bot-flows')}
          >
            Back to flows
          </Button>
        }
      />

      <div className="flex h-[calc(100vh-180px)] w-full overflow-hidden">
        {/* Left: wizard form */}
        <div className="flex w-full flex-col overflow-hidden lg:w-[60%] border-r border-border bg-surface">
          <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-8 lg:py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.18 }}
                className="h-full"
              >
                {stepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="hidden lg:flex lg:w-[40%] flex-col bg-bg px-6 py-6">
          <div className="mb-4 flex items-center gap-2">
            <Bot size={14} className="text-primary" />
            <span className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
              Live preview
            </span>
          </div>
          <div className="flex-1 overflow-hidden">
            <WizardChatPreview flow={flow} highlightStep={step} />
          </div>
          <p className="mt-3 text-center text-[11px] text-text-muted">
            Sample replies shown in italic — not real customer input
          </p>
        </div>
      </div>
    </>
  );
}
